import { forwardRef, ReactNode } from 'react';
import { cx } from 'class-variance-authority';
import * as Accordion from '@radix-ui/react-accordion';

import { Box } from '../Box';
import { BaseTypeMap, Component, ComponentProps } from '../types/Component';

import * as styles from './styles';
import type { ItemHeaderProps } from './styles';
import { Typography } from '../Typography';

type AccordionItemTypeMap = BaseTypeMap<
  'div',
  Accordion.AccordionItemProps & ItemHeaderProps & { label: ReactNode }
>;

export type AccordionItemProps = ComponentProps<AccordionItemTypeMap>;

export const AccordionItem: Component<AccordionItemTypeMap> = forwardRef(
  function AccordionItem(
    { children, className, label, border, disabled, ...rest },
    ref
  ) {
    return (
      <Box
        as={Accordion.Item}
        className={cx(styles.item({}), className)}
        ref={ref}
        disabled={disabled}
        {...rest}
      >
        <Accordion.Header className={styles.itemHeader({ border })}>
          <Accordion.Trigger className="group flex w-full items-center justify-between gap-2 sm:gap-4">
            <Typography
              as="span"
              intent="label"
              weight="medium"
              size="lg"
              className="text-left"
            >
              {label}
            </Typography>
            {!disabled ? (
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="currenColor"
                className={cx(
                  'shrink-0 transition duration-150 ease-in-out group-aria-[expanded=true]:rotate-180'
                )}
                aria-hidden
              >
                <mask
                  id="mask0_6577_4370"
                  style={{ maskType: 'alpha' }}
                  maskUnits="userSpaceOnUse"
                  x="0"
                  y="0"
                  width="24"
                  height="24"
                >
                  <rect width="24" height="24" fill="currentColor" />
                </mask>
                <g mask="url(#mask0_6577_4370)">
                  <path
                    d="M11.9999 14.95C11.8666 14.95 11.7416 14.929 11.6249 14.887C11.5083 14.8456 11.3999 14.775 11.2999 14.675L6.67494 10.05C6.49161 9.86664 6.40427 9.63731 6.41294 9.36198C6.42094 9.08731 6.51661 8.85831 6.69994 8.67498C6.88327 8.49164 7.11661 8.39998 7.39994 8.39998C7.68327 8.39998 7.91661 8.49164 8.09994 8.67498L11.9999 12.575L15.9249 8.64998C16.1083 8.46664 16.3376 8.37898 16.6129 8.38698C16.8876 8.39564 17.1166 8.49164 17.2999 8.67498C17.4833 8.85831 17.5749 9.09164 17.5749 9.37498C17.5749 9.65831 17.4833 9.89164 17.2999 10.075L12.6999 14.675C12.5999 14.775 12.4916 14.8456 12.3749 14.887C12.2583 14.929 12.1333 14.95 11.9999 14.95Z"
                    fill="currentColor"
                  />
                </g>
              </svg>
            ) : null}
          </Accordion.Trigger>
        </Accordion.Header>
        <Accordion.Content className={styles.itemContent({ border })}>
          {children}
        </Accordion.Content>
      </Box>
    );
  }
);
